import { BASE_MARGIN, BodyTextAnchor } from "$components/DesignSystem"
import { LoginFormQuery } from "$relay/LoginFormQuery.graphql"
import jwtDecode from "jwt-decode"
import { useContext, useState } from "react"
import { Animated, TouchableOpacity, View } from "react-native"
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay"

import { AuthModalContext } from "./AuthModal"
import type { AuthModes } from "./AuthModal"
import { LoginSignupToggle } from "./LoginSignupToggle"
import { MainAuthForm } from "./MainAuthForm"
import { ModalMascot } from "./ModalMascot"
import { UserSelectorForm } from "./UserSelectorForm"
import { useTransitionInAndOut } from "./useTransitionInAndOut"
import { AppContext } from "../../../AppContext"
import { useIsMobile } from "../../hooks/useIsMobile"

import { themes } from "$consts/themes"

export function LoginForm(props: { transitionOut: boolean; transitionOnEnter: boolean; updateViewMode: (mode: AuthModes) => void }) {
  const mobile = useIsMobile()
  const { updateViewMode } = props
  const theme = themes[0]
  const [users, setUsers] = useState<LoginFormQuery["response"]["usersOnAnAccount"]>([])

  const [usersOnAccount, setUsersOnAccount] = useState<number | null>(null)
  const ctx = useContext(AppContext)
  const env = useRelayEnvironment()
  const modalCtx = useContext(AuthModalContext)

  const [errorMessage, setErrorMessage] = useState<string>("")
  const [loading, setLoading] = useState(false)

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")

  const { height, opacity, translate } = useTransitionInAndOut({
    transitionOnEnter: props.transitionOnEnter,
    transitionOut: props.transitionOut,
    side: "left",
  })

  const onSubmitLogin = async () => {
    setErrorMessage("")
    setLoading(true)

    try {
      const result = await ctx.apiClient.login(email, password)
      if (result && "userInfoAccessToken" in result) {
        const jwtData = jwtDecode(result.userInfoAccessToken) as any
        setUsersOnAccount(jwtData.userCount || 2)
        const data = await fetchQuery<LoginFormQuery>(env, UsersQuery, { jwt: result.userInfoAccessToken }).toPromise()
        if (data) {
          setUsers(data.usersOnAnAccount)
        } else {
          setErrorMessage("Could get get users for account, please contact support")
          setLoading(false)
        }
      } else {
        if (ctx.apiClient.authed()) {
          modalCtx.closeModal()
        } else {
          console.log("not logged in?")
        }
      }
    } catch (error) {
      console.log("error", error)
      setErrorMessage("Could not log in with those details")
      setLoading(false)
    }
  }
  if (usersOnAccount) {
    return <UserSelectorForm usersOnAccount={usersOnAccount} email={email} password={password} users={users} />
  }

  return (
    <>
      <LoginSignupToggle updateViewMode={updateViewMode} active="login" />
      <Animated.View style={{ opacity, transform: [{ translateX: translate }], flexDirection: mobile ? "column-reverse" : "row" }}>
        <View style={{ flex: mobile ? undefined : 5 }}>
          <MainAuthForm
            placeholders={{
              email: "Your account email",
              password: "Your account password",
            }}
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            actionButtonText={"Log in"}
            errorMessage={errorMessage}
            loading={loading}
            onSubmitLogin={onSubmitLogin}
          />
          <Animated.View
            style={{
              height,
              opacity,
              overflow: "hidden",
            }}
          >
            <TouchableOpacity
              accessibilityRole="button"
              style={{ marginTop: BASE_MARGIN * 2 }}
              onPress={() => updateViewMode("forgot-password")}
            >
              <BodyTextAnchor style={{ textAlign: "center", color: theme.a_anchor }}>Lost email/password?</BodyTextAnchor>
            </TouchableOpacity>
          </Animated.View>
        </View>
        <ModalMascot screen="login" hasError={Boolean(errorMessage)} publicText={email} privateText={password} />
      </Animated.View>
    </>
  )
}

const UsersQuery = graphql`
  query LoginFormQuery($jwt: String!) {
    usersOnAnAccount(jwt: $jwt) {
      id
      name
      username
      usernameID
      avatarURL
    }
  }
`
